import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { Breadcrumb } from '@/components/common/Breadcrumb';
import { PageHeader } from '@/components/common/PageHeader';
import { QueryErrorView } from '@/components/common/QueryErrorView';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingRows } from '@/components/shared/LoadingRows';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { useGetTemplatesQuery } from '../api/agentsApi';
import { TemplateCard } from '../components/TemplateCard';
import { AGENT_TEMPLATE_KIND_LABELS } from '../labels';
import { AGENT_TEMPLATE_KINDS } from '../types';
import { docId } from '../utils';

const ALL_KINDS = 'all';

/**
 * Template gallery (design §6) — route `/organizations/:orgId/agents/new`.
 * Job: pick a template in ≤30s. Kind tabs drive `GET /agent/templates?kind=`
 * ("All templates" omits the param); cards render availability from the STEP
 * REGISTRY (§6.4, computed — never a hardcoded kind list) and still navigate
 * when unavailable — the wizard placeholder + review gate explain in context.
 * A `?projectId=` origin is persisted into every wizard link (§5, G4).
 */
export function TemplateGalleryPage() {
  const { orgId = '' } = useParams<{ orgId: string }>();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId') ?? undefined;
  const [activeKind, setActiveKind] = useState<string>(ALL_KINDS);

  const { data, isLoading, isError, error, refetch } = useGetTemplatesQuery({
    kind: activeKind === ALL_KINDS ? undefined : activeKind,
  });

  const templates = data ?? [];
  const galleryPath = `/organizations/${orgId}/agents/new${
    projectId ? `?projectId=${encodeURIComponent(projectId)}` : ''
  }`;

  return (
    <section aria-labelledby="gallery-title" className="space-y-6">
      <Breadcrumb
        items={[
          { label: 'Organization', to: `/organizations/${orgId}` },
          { label: 'Agents', to: `/organizations/${orgId}/agents` },
          { label: 'New agent' },
        ]}
      />

      <PageHeader
        title="New agent"
        description="Start from a template. You'll name the agent, pick its model, and attach what it needs."
        titleId="gallery-title"
      />

      <Tabs value={activeKind} onValueChange={setActiveKind}>
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value={ALL_KINDS}>All templates</TabsTrigger>
          {AGENT_TEMPLATE_KINDS.map((kind) => (
            <TabsTrigger key={kind} value={kind}>
              {AGENT_TEMPLATE_KIND_LABELS[kind]}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {isLoading ? (
        <LoadingRows label="Loading templates" columns={3} />
      ) : isError ? (
        <QueryErrorView error={error} label="templates" onRetry={() => void refetch()} />
      ) : templates.length === 0 ? (
        <EmptyState
          title="No templates"
          description="Templates are provided by your platform. None are available yet."
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {templates.map((template) => (
            <TemplateCard
              key={docId(template) || template.name}
              template={template}
              to={`${galleryPath}/${docId(template)}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
